import { useForm, UseFormProps, FieldValues, Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface UseFormBaseOptions<T extends FieldValues> extends UseFormProps<T> {
  /** Zod schema for validation */
  schema?: z.ZodSchema<T>;
  /** Success message to show on form submission */
  successMessage?: string;
  /** Error message to show on form submission failure */
  errorMessage?: string;
  /** Callback called on successful form submission */
  onSuccess?: (data: T) => void | Promise<void>;
  /** Callback called on form submission error */
  onError?: (error: Error) => void;
}

/**
 * Base hook for consistent form handling across the application
 * Provides common form patterns: validation, loading states, error handling, and toast notifications
 */
export const useFormBase = <T extends FieldValues>({
  schema,
  successMessage = 'Operation completed successfully',
  errorMessage = 'An error occurred. Please try again.',
  onSuccess,
  onError,
  ...formOptions
}: UseFormBaseOptions<T> = {}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<T>({
    resolver: schema ? zodResolver(schema) : undefined,
    ...formOptions,
  });

  /**
   * Handles form submission with consistent error handling and loading states
   */
  const handleSubmit = (submitFn: (data: T) => Promise<void> | void) => {
    return form.handleSubmit(async (data) => {
      setIsSubmitting(true);
      try {
        await submitFn(data);
        
        toast({
          title: 'Success',
          description: successMessage,
        });

        if (onSuccess) {
          await onSuccess(data);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        
        const message = error instanceof Error ? error.message : errorMessage;
        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });

        if (onError) {
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  /**
   * Sets form field value with type safety
   */
  const setFieldValue = <K extends Path<T>>(name: K, value: T[K]) => {
    form.setValue(name, value);
  };

  /**
   * Gets form field error message
   */
  const getFieldError = <K extends Path<T>>(name: K): string | undefined => {
    return form.formState.errors[name]?.message as string | undefined;
  };

  /**
   * Resets form with optional default values
   */
  const resetForm = (defaultValues?: Partial<T>) => {
    form.reset(defaultValues as T);
    setIsSubmitting(false);
  };

  return {
    form,
    isSubmitting,
    handleSubmit,
    setFieldValue,
    getFieldError,
    resetForm,
    // Expose commonly used form properties
    register: form.register,
    control: form.control,
    watch: form.watch,
    setValue: form.setValue,
    getValues: form.getValues,
    formState: form.formState,
  };
};

export default useFormBase;